import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import ReviewerOptIn from '../../../components/ReviewerOptIn';
import TermsPrivacyModal from '../../../components/TermsPrivacyModal';
import { useTranslation } from 'react-i18next';

const AccountSecurityStep = ({ formData, errors, onChange }) => {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showTerms, setShowTerms] = useState(false);

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 12) strength++;
    if (password?.length >= 16) strength++;
    if (/[a-z]/?.test(password) && /[A-Z]/?.test(password)) strength++;
    if (/\d/?.test(password)) strength++;
    if (/[^a-zA-Z0-9]/?.test(password)) strength++;
    return strength;
  };

  const handlePasswordChange = (value) => {
    onChange('password', value);
    setPasswordStrength(calculatePasswordStrength(value));
  };

  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-error';
    if (passwordStrength <= 3) return 'bg-warning';
    return 'bg-success';
  };

  const getStrengthLabel = () => {
    if (passwordStrength <= 1) return 'Weak';
    if (passwordStrength <= 3) return 'Medium';
    return 'Strong';
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="ShieldCheck" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Password Requirements</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Minimum 12 characters (16+ recommended)</li>
              <li>• Mix of uppercase and lowercase letters</li>
              <li>• At least one number</li>
              <li>• At least one special character (!@#$%^&amp;*)</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="relative">
        <Input
          label={t('registration.passwordLabel')}
          type={showPassword ? 'text' : 'password'}
          placeholder={t('registration.passwordPlaceholder')}
          value={formData?.password}
          onChange={(e) => handlePasswordChange(e?.target?.value)}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      {formData?.password && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Password Strength:</span>
            <span className={`font-medium ${passwordStrength <= 1 ? 'text-error' : passwordStrength <= 3 ? 'text-warning' : 'text-success'}`}>
              {getStrengthLabel()}
            </span>
          </div>
          <div className="h-2 bg-muted overflow-hidden">
            <div
              className={`h-full transition-all duration-250 ${getStrengthColor()}`}
              style={{ width: `${(passwordStrength / 5) * 100}%` }}
            />
          </div>
        </div>
      )}
      <div className="relative">
        <Input
          label={t('registration.confirmPasswordLabel')}
          type={showConfirmPassword ? 'text' : 'password'}
          placeholder={t('registration.confirmPasswordPlaceholder')}
          value={formData?.confirmPassword}
          onChange={(e) => onChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
        </button>
      </div>
      <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Mail" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-foreground mb-1">Email Verification Required</h4>
            <p className="text-xs text-muted-foreground">
              After registration, you'll receive a verification email. Please verify your email address to activate your account and access all features.
            </p>
          </div>
        </div>
      </div>
      <ReviewerOptIn
        available={formData?.availableAsReviewer}
        keywords={formData?.reviewerKeywords}
        onAvailableChange={(v) => onChange('availableAsReviewer', v)}
        onKeywordsChange={(kw) => onChange('reviewerKeywords', kw)}
      />

      <div className="space-y-3 pt-2">
        <Checkbox
          label={t('registration.termsAgree')}
          checked={formData?.agreeToTerms}
          onChange={(e) => onChange('agreeToTerms', e?.target?.checked)}
          required
        />
        <button
          type="button"
          onClick={() => setShowTerms(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline ml-6"
        >
          <Icon name="FileText" size={14} />
          {t('registration.readTerms')}
        </button>
        {errors?.agreeToTerms && (
          <p className="text-sm text-error">{errors?.agreeToTerms}</p>
        )}

        <Checkbox
          label="I want to receive updates about conferences and platform features"
          checked={formData?.receiveUpdates}
          onChange={(e) => onChange('receiveUpdates', e?.target?.checked)}
        />
      </div>

      <TermsPrivacyModal open={showTerms} onClose={() => setShowTerms(false)} />
    </div>
  );
};

export default AccountSecurityStep;